import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../api";
import { SectionTitle, StatusPill, EmptyState } from "../../components/Atoms";
import { Plus, PencilSimple, Trash, Briefcase, Globe, EnvelopeSimple, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { company:"", contact_person:"", email:"", phone:"", website:"", industry:"", logo:"", status:"Active" };

export default function ClientsPage() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => { const { data } = await api.get("/clients"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c =>
      (c.company || "").toLowerCase().includes(q) ||
      (c.contact_person || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  return (
    <div className="space-y-5">
      <SectionTitle title="Clients" subtitle="Brands running campaigns on CrewHQ"
        action={<button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="new-client-btn"><Plus size={16} weight="bold" /> New</button>} />

      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by client or company name…"
          className="input-base pl-9"
          data-testid="client-search-input"
        />
      </div>

      {filtered.length === 0 ? <EmptyState icon={Briefcase} title={search ? "No brands match your search" : "No brands yet"} subtitle={search ? "Try a different search term." : "Add your first client to get started."} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <div key={c.id} className="glass p-5 hover-lift" data-testid={`client-card-${c.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center overflow-hidden">
                  {c.logo ? <img src={c.logo} alt={c.company} className="w-full h-full object-cover" /> :
                    <Briefcase size={20} weight="fill" color="white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate">{c.company}</div>
                  <div className="text-xs text-muted truncate">{c.industry || "—"}</div>
                </div>
                <StatusPill status={c.status} />
              </div>
              <div className="space-y-1.5 text-sm text-muted">
                <div className="flex items-center gap-2 truncate"><EnvelopeSimple size={14} /> {c.email}</div>
                {c.website && <div className="flex items-center gap-2 truncate"><Globe size={14} /> {c.website}</div>}
              </div>
              <div className="flex gap-2 mt-4">
                <Link to={`/admin/clients/${c.id}`} className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm" data-testid={`open-client-${c.id}`}>
                  View projects <ArrowRight size={14} weight="bold" />
                </Link>
                <button onClick={() => { setEditing(c.id); setForm({ ...EMPTY, ...c }); setOpen(true); }} className="btn-ghost text-sm flex items-center justify-center gap-1.5" data-testid={`edit-client-${c.id}`}><PencilSimple size={14} /></button>
                <button onClick={() => remove(c.id)} className="btn-ghost text-[#EC4899] text-sm flex items-center justify-center gap-1.5" data-testid={`delete-client-${c.id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <form onSubmit={save} className="glass relative p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-white text-xl font-bold mb-2">{editing ? "Edit Client" : "New Client"}</div>
            {["company","contact_person","email","phone","website","industry","logo"].map(k => (
              <div key={k}>
                <label className="text-xs uppercase font-bold text-muted tracking-wider">{k.replace("_"," ")}</label>
                <input value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} className="input-base mt-1" data-testid={`client-input-${k}`} required={["company","contact_person","email"].includes(k)} />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="input-base mt-1">
                <option>Active</option><option>Inactive</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="save-client-btn">{editing ? "Save changes" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
